import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { supabase, Profile } from '@/lib/supabase';
import { Search, UserPlus, Filter, Award, Plus, Minus, Users, FolderOpen, CreditCard, Edit2, Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  created_at: string;
}

interface Payment {
  id: string;
  student_id: string;
  course_id: string | null;
  amount: number;
  status: string;
  payment_method: string;
  transaction_id: string | null;
  created_at: string;
  profiles: {
    full_name: string;
    email: string;
  };
  courses: {
    title: string;
  } | null;
}

export default function UsersPage() {
  const { profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [showPointsDialog, setShowPointsDialog] = useState(false);
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsReason, setPointsReason] = useState('');
  const [pointsType, setPointsType] = useState<'add' | 'remove'>('add');
  const [processing, setProcessing] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [categorySlug, setCategorySlug] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');

  const [payments, setPayments] = useState<Payment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [searchPayment, setSearchPayment] = useState('');

  const [showUserDialog, setShowUserDialog] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserFullName, setNewUserFullName] = useState('');
  const [newUserRole, setNewUserRole] = useState('studente');

  useEffect(() => {
    if (!authLoading && profile?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [profile, authLoading, navigate]);

  useEffect(() => {
    if (profile?.role === 'admin') {
      fetchUsers();
    }
  }, [profile]);

  const fetchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (data) {
      setUsers(data);
    }
    setLoading(false);
  };

  const filteredUsers = users.filter(user =>
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenPointsDialog = (user: Profile, type: 'add' | 'remove') => {
    setSelectedUser(user);
    setPointsType(type);
    setPointsAmount('');
    setPointsReason('');
    setShowPointsDialog(true);
  };

  const handleAssignPoints = async () => {
    if (!selectedUser || !pointsAmount || !pointsReason) {
      toast({
        title: 'Errore',
        description: 'Compila tutti i campi',
        variant: 'destructive',
      });
      return;
    }

    const points = parseInt(pointsAmount);
    if (isNaN(points) || points <= 0) {
      toast({
        title: 'Errore',
        description: 'Inserisci un numero valido di punti',
        variant: 'destructive',
      });
      return;
    }

    setProcessing(true);
    try {
      const { error } = await supabase
        .from('reward_points_transactions')
        .insert({
          student_id: selectedUser.id,
          admin_id: profile?.id,
          points: pointsType === 'add' ? points : -points,
          reason: pointsReason,
          type: 'adjusted',
        });

      if (error) throw error;

      toast({
        title: 'Successo',
        description: `${points} punti ${pointsType === 'add' ? 'aggiunti' : 'rimossi'} con successo`,
      });

      setShowPointsDialog(false);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile assegnare i punti',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const fetchCategories = async () => {
    setLoadingCategories(true);
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('name');

    if (data) {
      setCategories(data);
    }
    setLoadingCategories(false);
  };

  const handleOpenCategoryDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setCategoryName(category.name);
      setCategorySlug(category.slug);
      setCategoryDescription(category.description || '');
    } else {
      setEditingCategory(null);
      setCategoryName('');
      setCategorySlug('');
      setCategoryDescription('');
    }
    setShowCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryName || !categorySlug) {
      toast({
        title: 'Errore',
        description: 'Nome e slug sono obbligatori',
        variant: 'destructive',
      });
      return;
    }

    setProcessing(true);
    try {
      if (editingCategory) {
        const { error } = await supabase
          .from('categories')
          .update({
            name: categoryName,
            slug: categorySlug,
            description: categoryDescription || null,
          })
          .eq('id', editingCategory.id);

        if (error) throw error;

        toast({
          title: 'Successo',
          description: 'Categoria aggiornata con successo',
        });
      } else {
        const { error } = await supabase
          .from('categories')
          .insert({
            name: categoryName,
            slug: categorySlug,
            description: categoryDescription || null,
          });

        if (error) throw error;

        toast({
          title: 'Successo',
          description: 'Categoria creata con successo',
        });
      }

      setShowCategoryDialog(false);
      fetchCategories();
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile salvare la categoria',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa categoria?')) return;

    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Successo',
        description: 'Categoria eliminata con successo',
      });

      fetchCategories();
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile eliminare la categoria',
        variant: 'destructive',
      });
    }
  };

  const fetchPayments = async () => {
    setLoadingPayments(true);
    const { data, error } = await supabase
      .from('payments')
      .select(`
        *,
        profiles:student_id(full_name, email),
        courses:course_id(title)
      `)
      .order('created_at', { ascending: false });

    if (data) {
      setPayments(data as any);
    }
    setLoadingPayments(false);
  };

  const filteredPayments = payments.filter(payment =>
    payment.profiles?.full_name?.toLowerCase().includes(searchPayment.toLowerCase()) ||
    payment.profiles?.email?.toLowerCase().includes(searchPayment.toLowerCase()) ||
    payment.transaction_id?.toLowerCase().includes(searchPayment.toLowerCase())
  );

  const handleOpenUserDialog = () => {
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUserFullName('');
    setNewUserRole('studente');
    setShowUserDialog(true);
  };

  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserPassword || !newUserFullName || !newUserRole) {
      toast({
        title: 'Errore',
        description: 'Compila tutti i campi',
        variant: 'destructive',
      });
      return;
    }

    if (newUserPassword.length < 6) {
      toast({
        title: 'Errore',
        description: 'La password deve essere di almeno 6 caratteri',
        variant: 'destructive',
      });
      return;
    }

    setProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Non autenticato');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-user`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newUserEmail,
          password: newUserPassword,
          fullName: newUserFullName,
          role: newUserRole,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Impossibile creare l\'utente');
      }

      toast({
        title: 'Successo',
        description: 'Utente creato con successo',
      });

      setShowUserDialog(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile creare l\'utente',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  if (authLoading || profile?.role !== 'admin') {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Pannello Amministrazione
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Gestisci utenti, categorie e visualizza i pagamenti
          </p>
        </div>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Utenti
            </TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-2" onClick={() => fetchCategories()}>
              <FolderOpen className="w-4 h-4" />
              Categorie
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-2" onClick={() => fetchPayments()}>
              <CreditCard className="w-4 h-4" />
              Pagamenti
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Gestione Utenti</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Gestisci tutti gli utenti della piattaforma
                </p>
              </div>
              <Button onClick={handleOpenUserDialog}>
                <UserPlus className="w-4 h-4 mr-2" />
                Aggiungi utente
              </Button>
            </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Cerca per nome o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline">
                <Filter className="w-4 h-4 mr-2" />
                Filtri
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utente</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Ruolo</TableHead>
                      <TableHead>Punti Premio</TableHead>
                      <TableHead>Data registrazione</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                              {user.full_name?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <span className="font-medium">{user.full_name || 'N/A'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">{user.email}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              user.role === 'admin'
                                ? 'destructive'
                                : user.role === 'docente'
                                ? 'default'
                                : 'secondary'
                            }
                            className="capitalize"
                          >
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-yellow-600" />
                            <span className="font-semibold text-yellow-600">
                              {user.reward_points || 0}
                            </span>
                            {user.role === 'studente' && (
                              <div className="flex gap-1 ml-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleOpenPointsDialog(user, 'add')}
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleOpenPointsDialog(user, 'remove')}
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {new Date(user.created_at).toLocaleDateString('it-IT')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            Dettagli
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="categories" className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Categorie Corsi</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Gestisci le categorie disponibili per i corsi
                </p>
              </div>
              <Button onClick={() => handleOpenCategoryDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Nuova categoria
              </Button>
            </div>

            <Card>
              <CardContent className="pt-6">
                {loadingCategories ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : categories.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Nessuna categoria trovata</p>
                    <Button onClick={() => handleOpenCategoryDialog()} className="mt-4">
                      Crea la prima categoria
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Slug</TableHead>
                          <TableHead>Descrizione</TableHead>
                          <TableHead>Data creazione</TableHead>
                          <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {categories.map((category) => (
                          <TableRow key={category.id}>
                            <TableCell className="font-medium">{category.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{category.slug}</Badge>
                            </TableCell>
                            <TableCell className="max-w-xs truncate text-gray-600">
                              {category.description || '-'}
                            </TableCell>
                            <TableCell className="text-gray-600">
                              {new Date(category.created_at).toLocaleDateString('it-IT')}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenCategoryDialog(category)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteCategory(category.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments" className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Pagamenti</h2>
              <p className="text-sm text-gray-600 mt-1">
                Visualizza tutti i pagamenti effettuati sulla piattaforma
              </p>
            </div>

            <Card>
              <CardHeader>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Cerca per utente o ID transazione..."
                    value={searchPayment}
                    onChange={(e) => setSearchPayment(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {loadingPayments ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : filteredPayments.length === 0 ? (
                  <div className="text-center py-12">
                    <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {searchPayment ? 'Nessun pagamento trovato' : 'Nessun pagamento registrato'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Studente</TableHead>
                          <TableHead>Corso</TableHead>
                          <TableHead>Importo</TableHead>
                          <TableHead>Stato</TableHead>
                          <TableHead>Metodo</TableHead>
                          <TableHead>ID Transazione</TableHead>
                          <TableHead>Data</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPayments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{payment.profiles?.full_name || 'N/A'}</div>
                                <div className="text-sm text-gray-600">{payment.profiles?.email}</div>
                              </div>
                            </TableCell>
                            <TableCell className="max-w-xs truncate">
                              {payment.courses?.title || '-'}
                            </TableCell>
                            <TableCell className="font-semibold">
                              {payment.amount.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  payment.status === 'completed'
                                    ? 'default'
                                    : payment.status === 'pending'
                                    ? 'secondary'
                                    : 'destructive'
                                }
                              >
                                {payment.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{payment.payment_method}</Badge>
                            </TableCell>
                            <TableCell className="text-xs text-gray-600 font-mono">
                              {payment.transaction_id || '-'}
                            </TableCell>
                            <TableCell className="text-gray-600">
                              {new Date(payment.created_at).toLocaleDateString('it-IT')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={showPointsDialog} onOpenChange={setShowPointsDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {pointsType === 'add' ? 'Aggiungi' : 'Rimuovi'} Punti Premio
              </DialogTitle>
              <DialogDescription>
                {pointsType === 'add' ? 'Aggiungi' : 'Rimuovi'} punti premio per {selectedUser?.full_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="points">Numero di punti</Label>
                <Input
                  id="points"
                  type="number"
                  min="1"
                  placeholder="Es. 10"
                  value={pointsAmount}
                  onChange={(e) => setPointsAmount(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  1 punto = 1 euro di sconto
                </p>
              </div>
              <div>
                <Label htmlFor="reason">Motivo</Label>
                <Textarea
                  id="reason"
                  placeholder="Descrivi il motivo dell'assegnazione punti..."
                  value={pointsReason}
                  onChange={(e) => setPointsReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowPointsDialog(false)}
                disabled={processing}
              >
                Annulla
              </Button>
              <Button onClick={handleAssignPoints} disabled={processing}>
                {processing ? 'Elaborazione...' : 'Conferma'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? 'Modifica categoria' : 'Nuova categoria'}
              </DialogTitle>
              <DialogDescription>
                {editingCategory ? 'Modifica i dettagli della categoria' : 'Crea una nuova categoria per i corsi'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="category-name">Nome categoria</Label>
                <Input
                  id="category-name"
                  placeholder="Es. Programmazione"
                  value={categoryName}
                  onChange={(e) => {
                    setCategoryName(e.target.value);
                    if (!editingCategory) {
                      setCategorySlug(e.target.value.toLowerCase().replace(/\s+/g, '-'));
                    }
                  }}
                />
              </div>
              <div>
                <Label htmlFor="category-slug">Slug</Label>
                <Input
                  id="category-slug"
                  placeholder="programmazione"
                  value={categorySlug}
                  onChange={(e) => setCategorySlug(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  URL-friendly identifier (es. programmazione, design, business)
                </p>
              </div>
              <div>
                <Label htmlFor="category-description">Descrizione</Label>
                <Textarea
                  id="category-description"
                  placeholder="Descrizione opzionale della categoria..."
                  value={categoryDescription}
                  onChange={(e) => setCategoryDescription(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCategoryDialog(false)}
                disabled={processing}
              >
                Annulla
              </Button>
              <Button onClick={handleSaveCategory} disabled={processing}>
                {processing ? 'Salvataggio...' : editingCategory ? 'Aggiorna' : 'Crea'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crea nuovo utente</DialogTitle>
              <DialogDescription>
                Inserisci i dati per creare un nuovo utente nella piattaforma
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="user-full-name">Nome completo</Label>
                <Input
                  id="user-full-name"
                  placeholder="Es. Mario Rossi"
                  value={newUserFullName}
                  onChange={(e) => setNewUserFullName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="user-email">Email</Label>
                <Input
                  id="user-email"
                  type="email"
                  placeholder="Es. mario.rossi@esempio.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="user-password">Password</Label>
                <Input
                  id="user-password"
                  type="password"
                  placeholder="Minimo 6 caratteri"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="user-role">Ruolo</Label>
                <select
                  id="user-role"
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="studente">Studente</option>
                  <option value="docente">Docente</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowUserDialog(false)}
                disabled={processing}
              >
                Annulla
              </Button>
              <Button onClick={handleCreateUser} disabled={processing}>
                {processing ? 'Creazione...' : 'Crea utente'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
